import React from 'react';
import { useAlert, formatAddress, useIsVisible } from '@uplift/ui-v2';

import { useQueryClient } from '@tanstack/react-query';
import { paymentsTabRoute } from '@uplift/constants';
import { useL10n } from '@uplift/hooks';

import { EditPaymentMethodModal, PayBalanceModal, TurnOffAutopayModal } from './modals';
import { useStateQuery, useUserBillingQuery, useUserInsurancesQuery } from '@uplift/queries';
import { useCurrentuser } from '@uplift/contexts';
import { CalendarDateService, isExceedingByMonth } from '@uplift/services';
import { CardDeclinedStatusType, CardExpirationStatusType, useSaveBilling } from '../queries';
import { InsuranceMetaGrouping, Payment } from '@uplift/api';


export type BillingContextType = {
  isAutopayOn: boolean;
  isMedicaid: boolean;
  payBalanceOnly: boolean;
  setPayBalanceOnly: (v: boolean) => void;
  payBalanceRef: React.RefObject<HTMLButtonElement>;
  cardEndingIn: string;
  addressFormatted: Record<string, string> | null;
  balance: number;
  lastCompletedPaymentAt: string | null;
  outstandingPaymentsCount: number | null;
  cardStatus: CardExpirationStatusType | CardDeclinedStatusType;
  cardExpiredOrDeclined: boolean;
  isCardMissing: boolean;
  handleEditPaymentMethod: () => void;

  handleAutopayOn: () => void;
  showTurnOffAutopayModal: boolean;
  showEditPaymentMethodModal: boolean;
  isBillingMissing: boolean;
  showPayBalanceModal: boolean;
  showPayBalanceStickyButton: boolean;
  showEditPaymentMethodButton: boolean;
  setShowTurnOffAutopayModal: (v: boolean) => void;
  setShowPayBalanceModal: (v: boolean) => void;
  handlePaymentMethodModalSubmit: (resourceIds: null | string[]) => void;
  toggleAutopayUpdate: () => Promise<void>;
  handleAutopayOff: () => void;
  handlePayBalanceSubmit: (resourceIds: null | string[]) => void;
  handlePayBalanceClick: () => void;
  setShowEditPaymentMethodModal: (v: boolean) => void;

  paymentsToPay?: null | Payment[];
  setPaymentsToPay: (p?: null | Payment[]) => void;
};


const BillingContext = React.createContext<BillingContextType | undefined>(undefined);


export const useBillingContext = () => {
  const context = React.useContext(BillingContext);

  if (!context) {
    throw new Error('useBillingContext must be used within a BillingContextProvider');
  }
  return context;
};


export const BillingContextProvider: React.FC = ({ children }) => {
  const me = useCurrentuser();
  const { data: billing } = useUserBillingQuery(me.id, { suspense: true });
  const [ paymentsToPay, setPaymentsToPay ] = React.useState<null | Payment[]>(null);
  const isAutopayOn = billing?.autopayEnabled ?? true;
  const [ showTurnOffAutopayModal, setShowTurnOffAutopayModal ] = React.useState(false);
  const [ showPayBalanceModal, setShowPayBalanceModal ] = React.useState(false);
  const [ showEditPaymentMethodModal, setShowEditPaymentMethodModal ] = React.useState(false);
  const [ payBalanceOnly, setPayBalanceOnly ] = React.useState(false);
  const { data: userInsurances } = useUserInsurancesQuery(me.id);
  const primaryInsurance = userInsurances?.find((insurance) => insurance.insurance.isSecondaryInsurance === false);
  const { payBalance, saveAutopayStatus } = useSaveBilling();
  const payBalanceParam = paymentsTabRoute.useQueryParam('payBalance');
  const payBalanceRef = React.useRef();
  const isPayBalanceBoxButtonVisible = useIsVisible(payBalanceRef);
  const l10n = useL10n();
  const snackbar = useAlert();
  const queryClient = useQueryClient();
  const { data: billingState } = useStateQuery(billing?.billing?.address.stateId, {
    suspense: true,
    enabled: !!billing?.billing?.address.stateId,
  });

  const addressFormatted = formatAddress({
    address1: billing?.billing?.address.line1,
    address2: billing?.billing?.address.line2,
    city: billing?.billing?.address.city,
    zip: billing?.billing?.address.zip,
  }, billingState);

  const cardStatus = React.useMemo((): CardExpirationStatusType | CardDeclinedStatusType => {
    if (billing?.expire) {
      return 'expired';
    }

    const expiration = billing ? CalendarDateService.date().year(billing?.expYear).month(billing?.expMonth - 1).startOf('M') : null;
    const expireSoon = expiration ? !isExceedingByMonth(expiration, 2) : false;

    if (expireSoon) {
      return 'expiresSoon';
    }
    if (billing?.declined) {
      return 'declined';
    }

    return null;
  }, [ billing ]);

  const cardExpiredOrDeclined = billing?.expire || billing?.declined;
  const cardBrand = billing?.brand.split(' ').map(x => `${x.charAt(0).toUpperCase()}${x.substring(1)}`).join(' ');
  const lastCompletedPaymentAt = billing?.lastCompletedPaymentAt && CalendarDateService.date(billing?.lastCompletedPaymentAt).format('MM/DD/YYYY');
  const outstandingPaymentsCount = billing?.outstandingPaymentsCount;
  const balance = billing?.outstandingBalanceCents || 0;
  let walletLabel: string | undefined;

  if (billing?.wallet === 'apple_pay') {
    walletLabel = l10n.accountSettings.billing.applePay;
  } else if (billing?.wallet === 'google_pay') {
    walletLabel = l10n.accountSettings.billing.googlePay;
  }
  const cardEndingIn = l10n.accountSettings.billing.cardEndingIn(walletLabel || cardBrand, billing?.last4);
  const isBillingMissing = !billing;
  const isMedicaid = primaryInsurance?.insuranceCompany?.metaGrouping === InsuranceMetaGrouping.medicaid;
  const showPayBalanceStickyButton = balance > 0 && !isPayBalanceBoxButtonVisible;
  const showEditPaymentMethodButton = !cardExpiredOrDeclined;
  const isCardMissing = !billing?.brand || !billing?.last4;

  React.useEffect(() => {
    if (payBalanceParam === 'true') {
      setPayBalanceOnly(true);
      setShowPayBalanceModal(true);
    }
  }, [ payBalanceParam ]);

  const tryToSaveAutopayStatus = async (autopayStatus: boolean) => {
    try {
      await saveAutopayStatus(autopayStatus);
      await queryClient.invalidateQueries({ queryKey: [ 'user', me.id, 'billing' ] });
      if (autopayStatus) {
        snackbar.success(l10n.accountSettings.billing.autopayIsOn);
      } else {
        snackbar.success(l10n.accountSettings.billing.autopayIsOff);
      }
    } catch (error) {
      await queryClient.invalidateQueries({ queryKey: [ 'user', me.id, 'billing' ] });
      snackbar.error(l10n.accountSettings.billing.autopayUpdateFailed);
    }
  };

  const tryToPayBalance = async (resourceIds: null | string[]) => {
    if (balance) {
      await payBalance(resourceIds);
      if (me.rules.nonpayment) {
        snackbar.success(l10n.accountSettings.billing.reactivatedAndPaid);
      } else {
        if (resourceIds) {
          if (resourceIds.length === 1) {
            snackbar.success(l10n.accountSettings.billing.paidOne);
          } else if (resourceIds.length > 1) {
            snackbar.success(l10n.accountSettings.billing.paidMultiple(resourceIds.length));
          }
        } else {
          snackbar.success(l10n.accountSettings.billing.balancePaid);
        }
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [ 'me' ] }),
        queryClient.invalidateQueries({ queryKey: [ 'user', me.id, 'billing' ] }),
        queryClient.invalidateQueries({ queryKey: [ 'patient', me.id, 'serviceItems' ] }),
      ]);
    }
  };

  const handleEditPaymentMethod = () => {
    setShowEditPaymentMethodModal(true);
  };

  const handlePaymentMethodModalSubmit = async (resourceIds: null | string[]) => {
    await tryToPayBalance(resourceIds);
    if (showPayBalanceModal && !isAutopayOn && !payBalanceOnly) {
      await tryToSaveAutopayStatus(true);
    }
    setShowEditPaymentMethodModal(false);
    setShowPayBalanceModal(false);
    setPaymentsToPay(null);
  };

  const handleAutopayOn = async () => {
    if (balance) {
      setPayBalanceOnly(false);
      setShowPayBalanceModal(true);
    } else {
      await tryToSaveAutopayStatus(true);
    }
    setPaymentsToPay(null);
  };

  const toggleAutopayUpdate = async () => {
    if (isAutopayOn) {
      setShowTurnOffAutopayModal(true);
    } else {
      await handleAutopayOn();
    }
  };

  const handleAutopayOff = async () => {
    await tryToSaveAutopayStatus(false);
    setShowTurnOffAutopayModal(false);
    setPaymentsToPay(null);
  };

  const handlePayBalanceSubmit = async (resourceIds: null | string[]) => {
    await tryToPayBalance(resourceIds);
    if (!isAutopayOn && !payBalanceOnly) {
      await tryToSaveAutopayStatus(true);
    }
    setShowPayBalanceModal(false);
    setPaymentsToPay(null);
  };

  const handlePayBalanceClick = () => {
    if (cardExpiredOrDeclined) {
      handleEditPaymentMethod();
      return;
    }
    setPayBalanceOnly(true);
    setShowPayBalanceModal(true);
    setPaymentsToPay(null);
  };

  const billingContext: BillingContextType = {
    isAutopayOn,
    isMedicaid,
    payBalanceOnly,
    setPayBalanceOnly,
    payBalanceRef,
    cardEndingIn,
    addressFormatted,
    balance,
    lastCompletedPaymentAt,
    outstandingPaymentsCount,
    cardStatus,
    cardExpiredOrDeclined,
    isCardMissing,
    handleEditPaymentMethod,

    showTurnOffAutopayModal,
    showEditPaymentMethodModal,
    isBillingMissing,

    showPayBalanceModal,
    setShowTurnOffAutopayModal,
    setShowPayBalanceModal,

    showPayBalanceStickyButton,
    showEditPaymentMethodButton,
    handlePaymentMethodModalSubmit,
    toggleAutopayUpdate,
    handleAutopayOff,
    handlePayBalanceSubmit,
    handlePayBalanceClick,
    handleAutopayOn,
    setShowEditPaymentMethodModal,

    paymentsToPay,
    setPaymentsToPay,
  };

  return (
    <BillingContext.Provider value={billingContext}>
      {showTurnOffAutopayModal && <TurnOffAutopayModal onSubmit={handleAutopayOff} onCancel={() => setShowTurnOffAutopayModal(false)} />}
      {showPayBalanceModal && <PayBalanceModal onSubmit={handlePayBalanceSubmit} onCancel={() => setShowPayBalanceModal(false)} />}
      {showEditPaymentMethodModal && <EditPaymentMethodModal balance={balance} onSubmit={handlePaymentMethodModalSubmit} onCancel={() => setShowEditPaymentMethodModal(false)} />}
      {children}
    </BillingContext.Provider>
  );
};
